import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import { trackEvent } from '../services/analytics';
import { submitEmailReminder } from '../services/api';

// ─── Post-upload anon-uploader capture ───────────────────────────────────
//
// Renders for anonymous users after at least one file upload has completed.
// Presents two conversion paths in one calm card:
//
//   Zone 1 (primary, always rendered):
//     Sign in with Google → cryptographic file-claim → files owned by the
//     new account AND TTL resets to 14 days from claim moment. The "14 days
//     from today" language in the sub-copy is the real payoff — Gilfoyle
//     verified handleClaimFile at lambda-file-metadata/main.go:2886 resets
//     the TTL to now + 14d, not extends-in-place.
//
//   Zone 2 (secondary, feature-flag gated):
//     Leave an email → 48h pre-expiry reminder → link back into the same
//     sign-in-and-claim flow. Lighter-touch capture for readers who won't
//     sign up in-session. Gated behind REACT_APP_EMAIL_REMINDER_ENABLED so
//     the entire zone stays out of the DOM until Gilfoyle ships the backend
//     Lambda + DDB row + SES cron. The api.ts stub throws until then.
//
// Voice constraints locked with Glenn 2026-07-02:
//   - No exclamation marks, no sparkle icons, no "Free" pill spam.
//   - No infinite pulse/rotate animations. Motion is enter/exit only.
//   - No engineer-nouns ("Usage Analytics" / "Storage Stats") — the anon
//     first-timer has no dashboard-need to shout about.
//   - Zone 2 promise is a hard commitment: ONE reminder, and this email
//     list is never reused for product updates. Preserving that promise is
//     load-bearing; if a future broadcast surface exists, it opts users in
//     from a different entry point.
//
// Preserved from the prior (heavier) implementation:
//   - Session-storage dismiss with re-open on new upload completion.
//     Business logic; users forgive one dismiss but expect the prompt to
//     come back when they upload again.
//   - `claim_prompt_shown` telemetry (Donald's L2 funnel numerator).
//   - `claimSigninClicked` localStorage buffer flushed by AuthContext
//     post-login (upper-funnel instrumentation).
//   - Poll fallback so uploads that complete outside completedFiles-context
//     updates still surface the card within ~2s.
// ─────────────────────────────────────────────────────────────────────────

interface PendingClaim {
  fileId: string;
  claimToken: string;
  fileName: string;
  timestamp: number;
}

// URL-presence gate: the whole Zone 2 email-capture affordance renders only
// when REACT_APP_EMAIL_REMINDER_URL is set. One env var, no drift between a
// "feature enabled" boolean and an "endpoint URL" — either the backend is
// wired or it isn't. Gilfoyle dark-launches by omitting the env var until
// the Lambda + DDB + SES cron are green.
const EMAIL_REMINDER_URL = process.env.REACT_APP_EMAIL_REMINDER_URL;

// RFC-lite email shape check — strict enough to catch typos ("user@gmailcom"),
// loose enough not to reject legit ccTLD or plus-address forms.
const looksLikeEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export const SignInToClaimBanner: React.FC = () => {
  const { isAuthenticated, signIn, getValidToken } = useAuth();
  const { completedFiles } = useUpload();
  const [pendingClaims, setPendingClaims] = useState<PendingClaim[]>([]);
  const [isDismissed, setIsDismissed] = useState(() => {
    return sessionStorage.getItem('claimBannerDismissed') === 'true';
  });

  // Email-capture state (only meaningful when EMAIL_REMINDER_ENABLED).
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Guard so the visibility-tracking event fires at most once per mount,
  // even though `loadPendingClaims` polls every 2s.
  const claimPromptTrackedRef = useRef<boolean>(false);

  const loadPendingClaims = useCallback(() => {
    if (isAuthenticated || isDismissed) {
      setPendingClaims([]);
      return;
    }

    try {
      const storedClaims = localStorage.getItem('pendingClaims');
      if (storedClaims) {
        const claims: PendingClaim[] = JSON.parse(storedClaims);
        const validClaims = claims.filter(
          claim => Date.now() - claim.timestamp < 24 * 60 * 60 * 1000
        );
        if (validClaims.length !== claims.length) {
          localStorage.setItem('pendingClaims', JSON.stringify(validClaims));
        }
        setPendingClaims(validClaims);
      } else {
        setPendingClaims([]);
      }
    } catch (error) {
      console.error('Failed to load pending claims:', error);
    }
  }, [isAuthenticated, isDismissed]);

  useEffect(() => {
    loadPendingClaims();
  }, [loadPendingClaims]);

  useEffect(() => {
    if (isAuthenticated) return;

    if (completedFiles > 0) {
      // Un-dismiss when new uploads complete — a fresh drop earns another
      // prompt even if the user dismissed a previous instance in the same
      // session.
      if (sessionStorage.getItem('claimBannerDismissed') === 'true') {
        sessionStorage.removeItem('claimBannerDismissed');
        setIsDismissed(false);
      }
      const timer = setTimeout(() => loadPendingClaims(), 100);
      return () => clearTimeout(timer);
    }

    const pollInterval = setInterval(() => loadPendingClaims(), 2000);
    return () => clearInterval(pollInterval);
  }, [completedFiles, isAuthenticated, loadPendingClaims]);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('claimBannerDismissed', 'true');
  };

  const handleSignIn = () => {
    try {
      localStorage.setItem('claimSigninClicked', String(Date.now()));
    } catch {
      // localStorage can throw in private mode; never break sign-in.
    }
    signIn();
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (emailStatus === 'submitting' || emailStatus === 'done') return;
    if (!looksLikeEmail(email)) {
      setEmailError('That doesn’t look like an email.');
      return;
    }
    setEmailStatus('submitting');
    setEmailError(null);
    try {
      const fileIds = pendingClaims.map(c => c.fileId);
      await submitEmailReminder(email.trim(), fileIds);
      setEmailStatus('done');
      // Funnel signal — Donald's Zone-2 numerator. Uploader is anonymous
      // (that's the whole capture premise), so allowAnon is required. The
      // backend allowlist in lambda-events/main.go must accept this event
      // AND permit an anon caller; without both, the beacon is silently
      // dropped at the API edge.
      trackEvent('email_reminder_captured', getValidToken, { allowAnon: true });
    } catch (err) {
      console.error('email reminder submit failed:', err);
      setEmailStatus('error');
      setEmailError(err instanceof Error ? err.message : 'Something went wrong. Try again in a moment.');
    }
  };

  // Fire `claim_prompt_shown` exactly once per mount, the first time the
  // visibility gate would pass. Mirrors the render gate so the event
  // tracks the actual "banner became visible" moment.
  useEffect(() => {
    if (claimPromptTrackedRef.current) return;
    if (isAuthenticated || isDismissed) return;
    if (pendingClaims.length === 0) return;
    if (sessionStorage.getItem('claimBannerDismissed') === 'true') return;
    claimPromptTrackedRef.current = true;
    trackEvent('claim_prompt_shown', getValidToken);
  }, [isAuthenticated, isDismissed, pendingClaims, getValidToken]);

  if (
    isAuthenticated ||
    isDismissed ||
    pendingClaims.length === 0 ||
    sessionStorage.getItem('claimBannerDismissed') === 'true'
  ) {
    return null;
  }

  const fileCount = pendingClaims.length;
  const fileWord = fileCount === 1 ? 'file' : 'files';
  const themWord = fileCount === 1 ? 'it' : 'them';

  return (
    <motion.div
      className="relative overflow-hidden rounded-2xl bg-white/[0.02] border border-white/8 px-5 py-5 md:px-6 md:py-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      {/* Dismiss X — top-right, quiet. Absolute-positioned so the two-zone
          stack below can breathe without reserving a column for it. */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* ─── Zone 1 — Sign in and keep them ─────────────────────────────── */}
      <div className="pr-8">
        <h3 className="text-lg font-semibold text-white leading-tight">
          Keep your uploads.
        </h3>
        <p className="mt-1 text-sm text-white/60 leading-relaxed max-w-prose">
          Signing in claims your {fileWord} and keeps {themWord}{' '}
          <span className="text-white/80 font-medium">another 14 days from today</span>{' '}
          — no matter how long {themWord}’ve been up.
        </p>
        <button
          onClick={handleSignIn}
          className="mt-3 inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-soundwave-500 hover:bg-soundwave-600 transition-colors"
        >
          Sign in with Google
        </button>
      </div>

      {/* ─── Zone 2 — Or leave an email (URL-presence gated) ────────────── */}
      {EMAIL_REMINDER_URL && (
        <div className="mt-5 pt-5 border-t border-white/8">
          <div className="text-xs text-white/50 tracking-wide uppercase font-medium">
            Not signing in?
          </div>

          {emailStatus === 'done' ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-white/70">
              <Check className="w-4 h-4 text-soundwave-400 flex-shrink-0" />
              <span>Got it. We’ll email you before your {fileWord} expire.</span>
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-white/70 leading-relaxed max-w-prose">
                Leave an email. We’ll send{' '}
                <span className="text-white font-medium">one</span> reminder 48 hours
                before your {fileWord} would expire — and that’s the only email you’ll
                get from us.
              </p>
              <form onSubmit={handleEmailSubmit} className="mt-3 flex items-stretch gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  placeholder="you@example.com"
                  disabled={emailStatus === 'submitting'}
                  aria-label="Your email"
                  className="flex-1 min-w-0 h-10 px-3 rounded-xl bg-white/[0.03] border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-soundwave-500/50 focus:bg-white/[0.05] transition-colors disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={emailStatus === 'submitting' || !email.trim()}
                  className="h-10 px-4 rounded-xl text-sm font-medium text-white/80 bg-white/[0.05] hover:bg-white/[0.10] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailStatus === 'submitting' ? 'Sending…' : 'Remind me'}
                </button>
              </form>
              {emailError && (
                <div className="mt-2 text-xs text-red-300/80">{emailError}</div>
              )}
            </>
          )}
        </div>
      )}
    </motion.div>
  );
};
